import { useMutation, useQuery } from '@apollo/client'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useOutletContext } from 'react-router-dom'

import { Button, CardBox, Fields, Row, Stack, Text, useAlert } from '../../components'
import { validations } from '../../components/form'
import { mutations, queries } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'

export function AdvisorPersonalDetailsForm() {
  const {
    setHasUnsavedChanges: trackUnsavedChanges,
    profileFormRef,
    onSubmitSuccess: onSuccess,
    onSubmitFail: onFail,
  } = useOutletContext()

  const { data: settingsData, loading: settingsLoading } = useQuery(queries.user.myAdvisorCustodianSettings)
  const existingSettings = settingsData?.myAdvisorCustodianSettings

  const { register, handleSubmit, formState, setError, getValues, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const { isDirty, isSubmitting, errors = {} } = formState
  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  useEffect(() => {
    trackUnsavedChanges(isDirty)
  }, [isDirty, trackUnsavedChanges])

  useEffect(() => {
    if (settingsLoading || !existingSettings) return
    // reset() repopulates values AND re-snapshots defaultValues, so isDirty
    // stays false after this async load instead of setValue() leaving the
    // form looking dirty against a stale (empty) defaultValues snapshot
    reset({
      advisorPhone: existingSettings.advisorPhone || '',
      address: {
        lineOne: existingSettings.address?.lineOne || '',
        city: existingSettings.address?.city || '',
        province: existingSettings.address?.province || '',
        postalCode: existingSettings.address?.postalCode || '',
      },
    })
  }, [settingsLoading, existingSettings, reset])

  const [updateAdvisorSettings] = useMutation(mutations.user.updateAdvisorCustodianSettings, {
    onCompleted: () => {
      setAlert({ message: 'Personal details updated', severity: 'success' })
      reset(getValues())
      onSuccess?.()
    },
    onError: (serverErrors) => {
      parseAndSetFormErrors(serverErrors, setError)
      setAlert({ message: 'Update failed', severity: 'error' })
      onFail?.()
    },
    refetchQueries: [{ query: queries.user.myAdvisorCustodianSettings }],
  })

  const onSubmit = async (data) => {
    updateAdvisorSettings({
      variables: {
        data: {
          custodianName: existingSettings?.custodianName ?? '',
          advisorName: existingSettings?.advisorName ?? '',
          advisorEmail: existingSettings?.advisorEmail ?? '',
          advisorPhone: data.advisorPhone,
          advisorDealerRepCode: existingSettings?.advisorDealerRepCode,
          address: {
            lineOne: data.address.lineOne,
            city: data.address.city,
            province: data.address.province,
            postalCode: data.address.postalCode,
          },
        },
      },
    })
  }

  const onInvalid = () => {
    onFail?.()
  }

  if (settingsLoading) return null

  return (
    <CardBox>
      <Text.H4>Personal Details</Text.H4>
      <br />
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} ref={profileFormRef}>
        <Stack>
          <Text.H6>Mailing Address</Text.H6>
          <Fields.Text
            inputProps={{ 'data-testid': 'addrLineOne' }}
            type="text"
            label="Address Line One *"
            error={!!errors.address?.lineOne}
            helperText={errors?.address?.lineOne?.message}
            defaultValue={existingSettings?.address?.lineOne}
            {...register('address.lineOne', { ...validations.required })}
          />
          <Row>
            <Fields.Text
              inputProps={{ 'data-testid': 'city' }}
              type="text"
              label="City *"
              error={!!errors.address?.city}
              helperText={errors?.address?.city?.message}
              defaultValue={existingSettings?.address?.city}
              {...register('address.city', { ...validations.required })}
            />
            <Fields.Text
              inputProps={{ 'data-testid': 'province' }}
              type="text"
              label="Province / State *"
              error={!!errors.address?.province}
              helperText={errors?.address?.province?.message}
              defaultValue={existingSettings?.address?.province}
              {...register('address.province', { ...validations.required })}
            />
            <Fields.Text
              inputProps={{ 'data-testid': 'postalCode' }}
              type="text"
              label="Postal Code / ZIP *"
              error={!!errors.address?.postalCode}
              helperText={errors?.address?.postalCode?.message}
              defaultValue={existingSettings?.address?.postalCode}
              {...register('address.postalCode', { ...validations.required })}
            />
          </Row>

          <Text.H6 sx={{ mt: 2 }}>Phone Number</Text.H6>
          <Fields.Text
            inputProps={{ 'data-testid': 'advisorPhone' }}
            type="text"
            label="Phone *"
            error={!!errors.advisorPhone}
            helperText={errors?.advisorPhone?.message}
            defaultValue={existingSettings?.advisorPhone}
            {...register('advisorPhone', { ...validations.required, ...validations.phoneNumber })}
          />

          <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
            <Button type="submit" disabled={isSubmitting} data-testid="updatePersonalDetailsButton">
              Update Details
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      </form>
    </CardBox>
  )
}
